import { useState } from 'react';
import { Mail, Phone, Building, Send, CheckCircle, Users, Briefcase, Sparkles, Target, Quote } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DIANA_IMAGE = 'https://customer-assets.emergentagent.com/job_stem-career-hub/artifacts/qfcdf9r9_jrdesignphotograpy_-8899.jpg';

const consultingServices = [
  {
    id: 'program-design',
    title: 'Program Design',
    description: 'Custom STEAM education and career development programs tailored to your organization\'s needs.',
    icon: Target
  },
  {
    id: 'leadership-dev',
    title: 'Leadership Development',
    description: 'Empower your technical leaders with the soft skills and strategic thinking they need to thrive.',
    icon: Users
  },
  {
    id: 'dei-stem',
    title: 'DEI in STEM',
    description: 'Create inclusive environments where all STEAM professionals can succeed and belong.',
    icon: Sparkles
  },
  {
    id: 'wellness-initiatives',
    title: 'Wellness Initiatives',
    description: 'Implement sustainable wellness programs that prevent burnout and boost productivity.',
    icon: Briefcase
  }
];

const Consulting = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    organization: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }
    if (!formData.message.trim()) newErrors.message = 'Message is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      await axios.post(`${API}/contact`, formData);
      setIsSubmitted(true);
      toast.success('Message sent successfully!');
    } catch (error) {
      toast.error('Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  return (
    <div data-testid="consulting-page">
      {/* Hero Section with Diana */}
      <section className="section-padding bg-gradient-to-br from-white via-gray-50 to-brand-turquoise/5 overflow-hidden">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <div>
              <span className="badge-lime inline-block mb-6">For Organizations</span>
              <h1 className="font-heading font-bold text-4xl sm:text-5xl text-brand-blue leading-tight mb-6">
                Consulting Services for STEAM Excellence
              </h1>
              <p className="text-lg text-gray-600 leading-relaxed mb-8">
                Partner with STEMJECTORY to build programs, develop leaders, and create 
                cultures where STEAM professionals thrive. We work with schools, 
                organizations, and communities.
              </p>
              
              {/* Quote from Diana */}
              <div className="bg-white rounded-2xl p-6 shadow-lg border-l-4 border-brand-magenta relative">
                <Quote className="absolute top-4 right-4 w-8 h-8 text-brand-magenta/20" />
                <p className="text-gray-700 italic mb-4 leading-relaxed">
                  &ldquo;Every organization has the potential to become a place where STEAM 
                  professionals not only succeed but truly flourish. Let me help you build that culture.&rdquo;
                </p>
                <p className="font-heading font-bold text-brand-blue">— Diana, Founder</p>
              </div>
            </div>

            {/* Right - Creative Image Layout */}
            <div className="relative">
              {/* Main Image with creative shape */}
              <div className="relative z-10">
                <div className="relative">
                  {/* Decorative frame */}
                  <div className="absolute -inset-4 bg-gradient-to-br from-brand-turquoise to-brand-lime rounded-[2rem] transform rotate-3 opacity-20"></div>
                  <div className="absolute -inset-4 bg-gradient-to-br from-brand-magenta to-brand-turquoise rounded-[2rem] transform -rotate-2 opacity-10"></div>
                  
                  {/* Image container with creative clip */}
                  <div className="relative rounded-[1.5rem] overflow-hidden shadow-2xl transform hover:scale-[1.02] transition-transform duration-500">
                    <img
                      src={DIANA_IMAGE}
                      alt="Diana - Founder of STEMJECTORY"
                      className="w-full h-auto object-cover"
                      data-testid="diana-consulting-image"
                    />
                    {/* Gradient overlay at bottom */}
                    <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-brand-blue/80 to-transparent"></div>
                    <div className="absolute bottom-6 left-6 right-6">
                      <p className="text-white font-heading font-bold text-lg">Diana</p>
                      <p className="text-white/80 text-sm">Career Strategist & Wellness Advocate</p>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Floating decorative elements */}
              <div className="absolute -top-6 -right-6 w-24 h-24 bg-brand-lime rounded-full opacity-40 blur-sm"></div>
              <div className="absolute -bottom-8 -left-8 w-32 h-32 bg-brand-turquoise rounded-full opacity-30 blur-sm"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="section-padding bg-white" data-testid="services-grid">
        <div className="container-custom">
          <h2 className="font-heading font-bold text-2xl sm:text-3xl text-brand-blue mb-10">
            How We Can Help
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {consultingServices.map((service) => {
              const Icon = service.icon;
              return (
                <div
                  key={service.id}
                  className="card-marketing group"
                  data-testid={`service-${service.id}`}
                >
                  <div className="w-14 h-14 rounded-xl bg-brand-turquoise/10 flex items-center justify-center mb-6 group-hover:bg-brand-turquoise/20 transition-colors">
                    <Icon className="w-7 h-7 text-brand-turquoise" />
                  </div>
                  <h3 className="font-heading font-bold text-xl text-brand-blue mb-3">
                    {service.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {service.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="section-padding bg-gray-50" data-testid="contact-section">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Left - Info */}
            <div>
              <h2 className="font-heading font-bold text-2xl sm:text-3xl text-brand-blue mb-6">
                Let's Start a Conversation
              </h2>
              <p className="text-gray-600 mb-8 leading-relaxed">
                Whether you're looking to develop a comprehensive program or need 
                guidance on a specific initiative, we're here to help. Fill out the 
                form and Diana will personally respond within 48 hours.
              </p>

              <div className="space-y-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-brand-turquoise/10 flex items-center justify-center">
                    <Mail className="w-5 h-5 text-brand-turquoise" />
                  </div>
                  <div>
                    <p className="font-medium text-brand-blue">Email</p>
                    <p className="text-gray-600">hello@stemjectory.com</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-brand-turquoise/10 flex items-center justify-center">
                    <Phone className="w-5 h-5 text-brand-turquoise" />
                  </div>
                  <div>
                    <p className="font-medium text-brand-blue">Phone</p>
                    <p className="text-gray-600">Available upon request</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right - Form */}
            <div>
              {isSubmitted ? (
                <div className="bg-white rounded-2xl p-12 text-center border border-gray-100" data-testid="success-message">
                  <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
                    <CheckCircle className="w-10 h-10 text-green-600" />
                  </div>
                  <h3 className="font-heading font-bold text-2xl text-brand-blue mb-4">
                    Message Received!
                  </h3>
                  <p className="text-gray-600 mb-6">
                    Thank you for reaching out. Diana will review your message and 
                    get back to you within 48 hours.
                  </p>
                  <button
                    onClick={() => {
                      setIsSubmitted(false);
                      setFormData({ name: '', email: '', organization: '', message: '' });
                    }}
                    className="text-brand-magenta font-medium hover:underline"
                  >
                    Send another message
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="bg-white rounded-2xl p-8 border border-gray-100" data-testid="contact-form">
                  <div className="space-y-6">
                    {/* Name */}
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-brand-blue mb-2">
                        Name *
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        className={`w-full px-4 py-3 rounded-lg border ${
                          errors.name ? 'border-red-400' : 'border-gray-200'
                        } focus:border-brand-turquoise focus:ring-2 focus:ring-brand-turquoise/20 transition-colors`}
                        placeholder="Your full name"
                        data-testid="input-name"
                      />
                      {errors.name && (
                        <p className="text-red-500 text-sm mt-1" data-testid="error-name">{errors.name}</p>
                      )}
                    </div>

                    {/* Email */}
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-brand-blue mb-2">
                        Email *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        className={`w-full px-4 py-3 rounded-lg border ${
                          errors.email ? 'border-red-400' : 'border-gray-200'
                        } focus:border-brand-turquoise focus:ring-2 focus:ring-brand-turquoise/20 transition-colors`}
                        placeholder="you@example.com"
                        data-testid="input-email"
                      />
                      {errors.email && (
                        <p className="text-red-500 text-sm mt-1" data-testid="error-email">{errors.email}</p>
                      )}
                    </div>

                    {/* Organization */}
                    <div>
                      <label htmlFor="organization" className="block text-sm font-medium text-brand-blue mb-2">
                        Organization <span className="text-gray-400">(optional)</span>
                      </label>
                      <input
                        type="text"
                        id="organization"
                        name="organization"
                        value={formData.organization}
                        onChange={handleChange}
                        className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-brand-turquoise focus:ring-2 focus:ring-brand-turquoise/20 transition-colors"
                        placeholder="Your company or school"
                        data-testid="input-organization"
                      />
                    </div>

                    {/* Message */}
                    <div>
                      <label htmlFor="message" className="block text-sm font-medium text-brand-blue mb-2">
                        Message *
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        rows={5}
                        className={`w-full px-4 py-3 rounded-lg border ${
                          errors.message ? 'border-red-400' : 'border-gray-200'
                        } focus:border-brand-turquoise focus:ring-2 focus:ring-brand-turquoise/20 transition-colors resize-none`}
                        placeholder="Tell us about your needs and goals..."
                        data-testid="input-message"
                      />
                      {errors.message && (
                        <p className="text-red-500 text-sm mt-1" data-testid="error-message">{errors.message}</p>
                      )}
                    </div>

                    {/* Submit */}
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-60"
                      data-testid="submit-button"
                    >
                      {isSubmitting ? (
                        <span>Sending...</span>
                      ) : (
                        <>
                          <Send className="w-5 h-5" />
                          <span>Send Message</span>
                        </>
                      )}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Consulting;
